import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { isIosPlatform } from "@src/appV2/lib";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode } from "react";

import { LocationStatusRowIcon } from "./LocationStatusRowIcon";

const STATUS_ROW_TITLES = {
  loading: "Verifying location...",
  connected: "Location verified",
  failed: "Location tracking issue",
} as const;

const STATUS_ROW_DESCRIPTIONS = {
  loading: "Hang on while we're reestablishing your location...",
  connected: "The workplace can now see you on the map again",
} as const;

function getFailedStatusText(errorReason: string | undefined): string {
  const baseMessage = "We could not confirm your location";
  const [alwaysPermission, preciseLocation] = isIosPlatform()
    ? ['"Always"', '"Precise Location"']
    : ['"Allow all the time"', '"Use precise location"'];
  const suffix = `Please make sure location services are enabled, location permission is set to ${alwaysPermission} with ${preciseLocation} enabled, and try keeping your app open.`;

  if (isDefined(errorReason)) {
    return `${baseMessage} ${errorReason}. ${suffix}`;
  }

  return `${baseMessage}. ${suffix}`;
}

interface LocationStatusRowProps {
  state: "loading" | "connected" | "failed";
  errorReason: string | undefined;
  action?: ReactNode;
}

export function LocationStatusRow(props: LocationStatusRowProps) {
  const { state, errorReason, action } = props;

  return (
    <Stack direction="row" spacing={4} alignItems="flex-start">
      <LocationStatusRowIcon state={state} />
      <Stack spacing={2}>
        <Text semibold variant="h6">
          {STATUS_ROW_TITLES[state]}
        </Text>
        <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
          {state === "failed" ? getFailedStatusText(errorReason) : STATUS_ROW_DESCRIPTIONS[state]}
        </Text>
        {action}
      </Stack>
    </Stack>
  );
}
